import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { fetchProperties, searchProperties } from "../services/api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../styles/Properties.css";
import NotFound from "../components/NotFound";
import { formatCurrency } from "../utils/formatCurrency";
import Pagination from "../components/Pagination";
import Chat from "../components/Chat";
import { formatUrl } from "../utils/config";

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Skeleton loader component
  const PropertySkeleton = () => (
    <div className="col-xl-4 col-md-6">
      <div className="project-card style1-1">
        <div className="project-card-img">
          <Skeleton height={300} />
        </div>
        <div className="project-card-details">
          <h4 className="box-title">
            <Skeleton width={200} />
          </h4>
          <Skeleton width={100} height={35} style={{ marginTop: "10px" }} />
        </div>
      </div>
    </div>
  );

  const handleSearchChange = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear any existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(async () => {
      if (!value.trim()) {
        setFilteredProperties(properties);
        return;
      }

      setLoading(true);
      try {
        const searchResults = await searchProperties(value);
        setFilteredProperties(
          Array.isArray(searchResults) ? searchResults : []
        );
      } catch (error) {
        console.error("Search failed:", error);
        setError(error.message);
        setFilteredProperties([]);
      } finally {
        setLoading(false);
      }
    }, 500); // Wait 500ms after user stops typing

    setTypingTimeout(timeout);
  };

  const getProperties = async () => {
    setLoading(true);
    try {
      const response = await fetchProperties(currentPage);
      if (response?.payload) {
        setProperties(response.payload.page);
        setFilteredProperties(response.payload.page);
        setTotalPages(response.payload.totalPages);
        setCurrentPage(response.payload.currentPage);
      }
    } catch (error) {
      console.error("Failed to fetch properties:", error);
      setError(error.message);
      setProperties([]);
      setFilteredProperties([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProperties();
    return () => {
      // Cleanup timeout on component unmount
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
    };
  }, [currentPage]);

  return (
    <>
      {/* <Header /> */}
      <div
        className="breadcumb-wrapper"
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">OUR Properties</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>Properties</li>
            </ul>
          </div>
        </div>
      </div>

      <Chat />
      <section className="project-sec space-top space-extra-bottom">
        <div className="container">
          <div className="row align-items-center mb-4">
            <div className="col-lg-6">
              <div className="title-area">
                <span className="sub-title6">OUR PROPERTIES</span>
                <h2 className="sec-title">See Our Available Properties</h2>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="search-area">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search properties..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  {loading && (
                    <span
                      className="position-absolute"
                      style={{
                        right: "60px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 4,
                      }}
                    >
                      <span className="spinner-border spinner-border-sm text-primary" />
                    </span>
                  )}
                  <button
                    className="btn btn-primary"
                    style={{
                      background: "var(--theme-color)",
                      border: "none",
                      padding: "0 25px",
                    }}
                    disabled={loading}
                  >
                    <i className="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="row gy-4">
            {loading && searchTerm === "" ? (
              [...Array(6)].map((_, index) => <PropertySkeleton key={index} />)
            ) : filteredProperties.length === 0 ? (
              <NotFound message="No properties found." />
            ) : (
              filteredProperties.map((property, index) => (
                <div key={index} className="col-xl-4 col-md-6">
                  <div className="project-card style1-1">
                    <div className="project-card-img">
                      <img
                        src={
                          formatUrl(property?.thumbnail) ||
                          "/assets/img/main/default.jpg"
                        }
                        alt={property?.title}
                        className="w-100"
                        onError={(e) =>
                          (e.target.src = "/assets/img/main/default.jpg")
                        }
                      />
                      <div
                        className="project-card-details property-card-hover"
                        style={{
                          transition: "all 0.3s ease-in-out",
                        }}
                      >
                        <h4 className="box-title">
                          <Link
                            to={`/properties/${property.id}`}
                            state={{ property: property }}
                            style={{
                              color: "inherit",
                              textDecoration: "none",
                              transition: "color 0.3s ease-in-out",
                            }}
                          >
                            {property.houseNumber} - {property.buildingType}
                          </Link>
                        </h4>
                        <p
                          className="mb-2"
                          style={{
                            transition: "color 0.3s ease-in-out",
                          }}
                        >
                          <i
                            className="fas fa-map-marker-alt me-2"
                            style={{
                              color: "inherit",
                            }}
                          ></i>
                          {property.estate.name}, Block {property.blockNumber}
                        </p>
                        <div
                          className="property-features mb-3 d-flex flex-wrap gap-2"
                          style={{
                            transition: "color 0.3s ease-in-out",
                          }}
                        >
                          <span className="me-3">
                            <i className="fas fa-bed me-1"></i>{" "}
                            {property.bedRooms} Beds
                          </span>
                          <span className="me-3">
                            <i className="fas fa-toilet me-1"></i>{" "}
                            {property.toilets} Baths
                          </span>
                          {property.livingRoom > 0 && (
                            <span className="me-3">
                              <i className="fas fa-couch me-1"></i>{" "}
                              {property.livingRoom} Living
                            </span>
                          )}
                          {property.kitchen > 0 && (
                            <span className="me-3">
                              <i className="fas fa-utensils me-1"></i>{" "}
                              {property.kitchen} Kitchen
                            </span>
                          )}
                          {property.dianning > 0 && (
                            <span>
                              <i className="fas fa-chair me-1"></i>{" "}
                              {property.dianning} Dining
                            </span>
                          )}
                        </div>
                        <Link
                          to={`/properties/${property.id}`}
                          state={{ property: property }}
                          className="th-btn style9"
                          style={{
                            transition: "all 0.3s ease-in-out",
                          }}
                        >
                          View Details <i className="fas fa-arrow-right ms-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {!loading && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                setCurrentPage(page);
                window.scrollTo(0, 0);
              }}
            />
          )}
        </div>
      </section>
      {/* <Footer /> */}
    </>
  );
};

export default Properties;
